import { useEffect, useState } from 'react';
import { platformSite } from '../data/http';
import { isPast, readVisits, type Visit, type VisitsResult, type VisitStatus } from '../data/visits';
import { dayCell, weekday } from '../lib/format';
import { planPath } from '../lib/route';
import type { PlansResult } from '../data/plans';
import { PlusIcon } from '../icons';

/* ВИЗИТЫ — главная страница кабинета: все записи через aventrae, прошедшие и
   предстоящие.

   ИСТОЧНИК ПОЯВИЛСЯ 13.09 — `GET /read/patient/appointment/feed`. До него у
   раздела не было ручки вовсе: все read-ручки `appointment-api` скоупились
   ВРАЧОМ через заголовок организации, а у пациента организации нет и не будет.
   Теперь есть своя, адресуемая аккаунтом, без единого параметра.

   ВЫДУМАННЫХ СТРОК ЗДЕСЬ НЕТ И НЕ БУДЕТ (решение человека 12.09). Пустая лента
   печатается пустой, а не данными макета: пациент читает не «это макет», а «мои
   записи» — и может прийти к врачу в день, которого никто не назначал.

   ПУСТО — ЗАКОННЫЙ ОТВЕТ, И ТЕКСТ ОБЪЯСНЯЕТ ПОЧЕМУ. Карта связывается с
   аккаунтом двумя способами: пациент записался сам, либо забрал ссылку на план
   и номер сошёлся. Человек, которого врач завёл руками и которому ссылку не
   присылал, увидит пустую ленту — и это не поломка, а устройство: искать карты
   по номеру домен не пытается, номер только подтверждает названную ссылкой. */

export function Visits({ plans }: { plans: PlansResult }) {
    const [result, setResult] = useState<VisitsResult | null>(null);
    /* Адрес публичных страниц может быть не задан — тогда кнопки нет вовсе.
       Неактивная кнопка «записаться» обещала бы действие, которого нет, а
       ссылка на прод из дев-сборки уводила бы пациента не туда. */
    const site = platformSite();

    useEffect(() => {
        let alive = true;
        readVisits().then((r) => {
            if (alive) setResult(r);
        });
        return () => {
            alive = false;
        };
    }, []);

    return (
        <>
            <div className="mh">
                <div>
                    <h1>Визиты</h1>
                    <div className="sub">все записи через aventrae, прошедшие и предстоящие</div>
                </div>
                {/* «Записаться» ведёт на публичный сайт: запись делает страница
                    врача — там выбор услуги, свободные окна и согласия. */}
                {site !== null && (
                    <div className="act">
                        <a className="btn pri" href={site}>
                            <PlusIcon />
                            записаться
                        </a>
                    </div>
                )}
            </div>

            <div className="cols">
                <div>{body(result, planByVisit(plans))}</div>
            </div>
        </>
    );
}

/* Визит → id его плана. Строится из ленты планов, потому что лента визитов про
   планы ничего не знает и знать не должна: это разные домены и разные сервисы.
   В карте только те планы, которые пациенту ВИДНЫ, — значит отсутствие ключа
   означает «кнопка неактивна», а не «плана нет»: план может существовать и быть
   ещё не открытым, и различить эти два случая кабинет не может и не должен. */
function planByVisit(plans: PlansResult): Map<string, string> {
    const m = new Map<string, string>();
    if (plans.state !== 'ok') return m;
    for (const p of plans.plans) if (p.appointmentId) m.set(p.appointmentId, p.id);
    return m;
}

function body(result: VisitsResult | null, planOf: Map<string, string>) {
    if (result === null) return <div className="empty">Загружаем визиты…</div>;
    if (result.state === 'offline') {
        return <div className="empty">Визиты не подключены в этом окружении.</div>;
    }
    if (result.state === 'forbidden') {
        return (
            <div className="empty">
                Кабинету пока не открыт доступ к визитам. Это настройка на стороне платформы —
                мы уже знаем.
            </div>
        );
    }
    if (result.state === 'error') {
        return <div className="empty">Не удалось загрузить визиты. Обновите страницу.</div>;
    }

    const now = Date.now();
    const upcoming = result.visits.filter((v) => !isPast(v, now));
    const past = result.visits.filter((v) => isPast(v, now));

    if (upcoming.length === 0 && past.length === 0) {
        return (
            <div className="empty">
                Записей пока нет. Они появятся здесь после того, как вы запишетесь через
                aventrae — или когда врач пришлёт вам ссылку на план ухода.
            </div>
        );
    }

    return (
        <>
            {upcoming.length > 0 ? (
                <>
                    <div className="vsec">Предстоящие</div>
                    <div className="vlist">
                        {/* Ближайшая первой: сервер отдаёт ленту от новых к старым,
                            и для будущего это обратный порядок. */}
                        {[...upcoming].reverse().map((v) => (
                            <Row key={v.id} v={v} planId={planOf.get(v.id) ?? null} />
                        ))}
                    </div>
                </>
            ) : null}
            {past.length > 0 ? (
                <>
                    <div className="vsec">Прошедшие</div>
                    <div className="vlist">
                        {past.map((v) => (
                            <Row key={v.id} v={v} planId={planOf.get(v.id) ?? null} />
                        ))}
                    </div>
                </>
            ) : null}
        </>
    );
}

function Row({ v, planId }: { v: Visit; planId: string | null }) {
    const cell = dayCell(v.startsAt);
    return (
        <div className="vrow">
            <span className="vday">
                <b>{cell.day}</b>
                <i>{cell.month}</i>
            </span>
            <span className="vtx">
                <b>{v.title || 'Приём'}</b>
                <span>
                    {[v.doctor.name, v.doctor.clinicName, weekday(v.startsAt)]
                        .filter(Boolean)
                        .join(', ')}
                </span>
            </span>
            <span className={`vst ${tone(v.status)}`}>{statusWord(v.status)}</span>
            {/* КНОПКА ЕСТЬ ВСЕГДА, кликабельна — только когда план виден
                (решение человека 16.09). Прятать её значило бы заставлять
                пациента гадать, бывает ли у визита план вообще; неактивная
                кнопка говорит «бывает, но не у этого». */}
            {planId !== null ? (
                <a className="vplan" href={planPath(planId)}>
                    план ухода
                </a>
            ) : (
                <span
                    className="vplan off"
                    title="Врач не присылал план по этому визиту или ещё не открыл его"
                >
                    план ухода
                </span>
            )}
        </div>
    );
}

/* Статус словом, а не кодом. `in_progress` сервер вычисляет от часов — колонки
   под него нет и быть не может. */
function statusWord(s: VisitStatus): string {
    switch (s) {
        case 'requested':
            return 'ждёт подтверждения';
        case 'confirmed':
            return 'подтверждён';
        case 'in_progress':
            return 'идёт сейчас';
        case 'completed':
            return 'проведён';
        case 'cancelled':
            return 'отменён';
        case 'declined':
            return 'врач отказал';
    }
}

const tone = (s: VisitStatus): string =>
    s === 'cancelled' || s === 'declined' ? 'off' : s === 'completed' ? 'done' : '';
